import React, { Suspense } from 'react';

import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

import { AuthProvider, useAuth } from './context/AuthContext';

import { BusinessProvider } from './context/BusinessContext';

import { LiveCallsFeedProvider } from './context/LiveCallsFeedContext';

import LoadingScreen from './components/LoadingScreen';

import PhoneLinkGate from './components/PhoneLinkGate';
import GoogleLinkGate from './components/GoogleLinkGate';



const MAINTENANCE_MODE = false;



// Lazy load all heavy components

const HomePage = React.lazy(() => import('./pages/HomePage'));

const LoginPage = React.lazy(() => import('./pages/LoginPage'));

const SuperOwlApp = React.lazy(() => import('./components/SuperOwlApp'));

const WebsiteChat = React.lazy(() => import('./components/WebsiteChat'));

const AboutPage = React.lazy(() => import('./pages/AboutPage'));

const FeaturesPage = React.lazy(() => import('./pages/FeaturesPage'));

const PricingPage = React.lazy(() => import('./pages/PricingPage'));

const TermsPage = React.lazy(() => import('./pages/TermsPage'));

const PrivacyPage = React.lazy(() => import('./pages/PrivacyPage'));

const CancellationRefundPage = React.lazy(() => import('./pages/CancellationRefundPage'));

const ShippingPage = React.lazy(() => import('./pages/ShippingPage'));

const ContactPage = React.lazy(() => import('./pages/ContactPage'));

const AccountSettingsPage = React.lazy(() => import('./pages/AccountSettingsPage'));

const RequestAccountDeletionPage = React.lazy(() => import('./pages/RequestAccountDeletionPage'));

const PaymentSuccessPage = React.lazy(() => import('./pages/PaymentSuccessPage'));



// Protected Route Component

const ProtectedRoute = ({ children }) => {

  const { user, loading } = useAuth();

  const location = useLocation();



  if (loading) {

    return <LoadingScreen />;

  }



  if (!user) {

    if (location.pathname !== '/') {

      return <Navigate to="/" />;

    }

  }



  return children;

};



function AppRoutes() {

  const { user } = useAuth();



  return (

    <BusinessProvider>

      <Suspense fallback={<LoadingScreen />}>

        <Routes>

          <Route path="/" element={<HomePage />} />

          <Route path="/login" element={<LoginPage />} />

          <Route path="/about" element={<AboutPage />} />

          <Route path="/features" element={<FeaturesPage />} />

          <Route path="/pricing" element={<PricingPage />} />

          <Route path="/terms" element={<TermsPage />} />

          <Route path="/privacy" element={<PrivacyPage />} />

          <Route path="/cancellation-refund" element={<CancellationRefundPage />} />

          <Route path="/shipping" element={<ShippingPage />} />

          <Route path="/contact" element={<ContactPage />} />

          <Route path="/request-account-deletion" element={<RequestAccountDeletionPage />} />

          <Route path="/chat/:businessIdentifier" element={<WebsiteChat />} />

          <Route

            path="/success"

            element={user ? <PaymentSuccessPage /> : <Navigate to="/login" />}

          />

          <Route

            path="/app/*"

            element={

              user ? (

                <LiveCallsFeedProvider>

                  <SuperOwlApp />

                </LiveCallsFeedProvider>

              ) : (

                <Navigate to="/login?redirect=/app&signin=google&from=mobile" />

              )

            }

          />

          <Route

            path="/account-settings"

            element={user ? <AccountSettingsPage /> : <Navigate to="/login" />}

          />

        </Routes>

      </Suspense>

    </BusinessProvider>

  );

}



// App content component that uses auth context

function AppContent() {

  const { user, refreshUser } = useAuth();



  // Must be before any early return — hooks must run unconditionally

  React.useEffect(() => {

    if (window.hideInitialLoading) {

      window.hideInitialLoading();

    }

  }, []);



  const routes = <AppRoutes />;



  // Email/Google accounts must link phone; phone-only accounts must link Gmail.
  if (user) {

    return (

      <PhoneLinkGate user={user} refreshUser={refreshUser}>

        <GoogleLinkGate user={user} refreshUser={refreshUser}>

          {routes}

        </GoogleLinkGate>

      </PhoneLinkGate>

    );

  }



  return routes;

}



function MaintenancePage() {

  return (

    <div style={{

      minHeight: '100vh',

      background: 'linear-gradient(135deg, #1d2840 0%, #2d4a7a 50%, #1d2840 100%)',

      display: 'flex',

      alignItems: 'center',

      justifyContent: 'center',

      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',

      padding: '20px',

    }}>

      <div style={{

        textAlign: 'center',

        color: '#fff',

        maxWidth: '480px',

      }}>

        <div style={{ fontSize: '64px', marginBottom: '16px' }}>🦉</div>

        <h1 style={{

          fontSize: '28px',

          fontWeight: '700',

          marginBottom: '12px',

          letterSpacing: '-0.5px',

        }}>

          We'll be back soon

        </h1>

        <p style={{

          fontSize: '16px',

          color: '#93c5fd',

          lineHeight: '1.6',

          marginBottom: '32px',

        }}>

          SuperOwl is undergoing scheduled maintenance to bring you a better experience.

          We expect to be back in a few hours.

        </p>

        <div style={{

          background: 'rgba(255,255,255,0.08)',

          border: '1px solid rgba(255,255,255,0.15)',

          borderRadius: '12px',

          padding: '16px 24px',

          fontSize: '14px',

          color: '#cbd5e1',

        }}>

          Questions? Reach us at{' '}

          <a href="mailto:ai.creativebuilds@gmail.com" style={{ color: '#3DA3DC', textDecoration: 'none' }}>

            ai.creativebuilds@gmail.com

          </a>

        </div>

        <p style={{ marginTop: '32px', fontSize: '13px', color: '#64748b' }}>

          — The SuperOwl Team

        </p>

      </div>

    </div>

  );

}



function App() {

  if (MAINTENANCE_MODE) {

    return <MaintenancePage />;

  }



  return (

    <AuthProvider>

      <AppContent />

    </AuthProvider>

  );

}



export default App;

